import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  Cancel,
  CheckCircle,
  ContentCopy,
  Edit,
  MailOutline,
  Send,
  Task,
  UploadFile,
  VerifiedUser,
  WarningAmber,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import {
  PageHeader,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import vendorOnboardingService, {
  ApplicantFields,
  DOCUMENT_TYPES,
  DocumentReviewStatus,
  DocumentType,
  MSME_CATEGORIES,
  VENDOR_TYPES,
  VerificationKind,
  VerificationStatus,
  VendorOnboardingFull,
  VendorOnboardingStatus,
  VendorType,
} from '../../services/vendorOnboardingService';

// ---------------------------------------------------------------------------
// Static reference tables
// ---------------------------------------------------------------------------

const FLOW_STEPS: VendorOnboardingStatus[] = [
  'DRAFT',
  'INVITED',
  'SUBMITTED',
  'UNDER_VERIFICATION',
  'PENDING_APPROVAL',
  'APPROVED',
];

const STATUS_LABEL: Record<VendorOnboardingStatus, string> = {
  DRAFT: 'Draft',
  INVITED: 'Invited',
  SUBMITTED: 'Submitted',
  UNDER_VERIFICATION: 'Under Verification',
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

const STATUS_TONE: Record<VendorOnboardingStatus, StatusTone> = {
  DRAFT: 'neutral',
  INVITED: 'info',
  SUBMITTED: 'info',
  UNDER_VERIFICATION: 'warning',
  PENDING_APPROVAL: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
};

const VERIFICATION_LABEL: Record<VerificationKind, string> = {
  GSTIN: 'GSTIN',
  PAN: 'PAN',
  UDYAM: 'UDYAM (MSME)',
  BANK_ACCOUNT: 'Bank Account',
  IFSC: 'IFSC',
};

/** Kinds resolvable from fields already stored on the application row. */
const AUTO_VALUE_KINDS: VerificationKind[] = ['GSTIN', 'PAN', 'UDYAM'];

const VERIFICATION_TONE: Record<VerificationStatus, StatusTone> = {
  VERIFIED: 'success',
  FAILED: 'error',
  // Deliberately NOT success and NOT error/warning — UNAVAILABLE means "this
  // cannot be confirmed offline", a fact about the check, not a bad result.
  UNAVAILABLE: 'info',
  PENDING: 'neutral',
};

const VERIFICATION_STATUS_LABEL: Record<VerificationStatus, string> = {
  VERIFIED: 'Verified',
  FAILED: 'Failed',
  UNAVAILABLE: 'Unavailable Offline',
  PENDING: 'Pending',
};

const DOCUMENT_STATUS_TONE: Record<string, StatusTone> = {
  PENDING: 'neutral',
  VERIFIED: 'success',
  REJECTED: 'error',
  EXPIRED: 'warning',
};

const DOCUMENT_TYPE_LABEL: Record<DocumentType, string> = {
  PAN_CARD: 'PAN Card',
  GST_CERTIFICATE: 'GST Certificate',
  UDYAM_CERTIFICATE: 'UDYAM Certificate',
  CANCELLED_CHEQUE: 'Cancelled Cheque',
  BANK_STATEMENT: 'Bank Statement',
  INCORPORATION_CERT: 'Incorporation Certificate',
  ADDRESS_PROOF: 'Address Proof',
  ISO_CERT: 'ISO Certificate',
  OTHER: 'Other',
};

const fmtDate = (value: string | null) => (value ? new Date(value).toLocaleDateString('en-IN') : '—');
const fmtDateTime = (value: string | null) =>
  value ? new Date(value).toLocaleString('en-IN') : '—';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const VendorOnboardingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [app, setApp] = useState<VendorOnboardingFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [noticeVendorId, setNoticeVendorId] = useState<number | null>(null);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Invite
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  // Edit applicant dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<ApplicantFields>({});
  const [editError, setEditError] = useState<string | null>(null);

  // Verification inline value inputs (BANK_ACCOUNT / IFSC have no stored column)
  const [verifyValues, setVerifyValues] = useState<Partial<Record<VerificationKind, string>>>({});
  const [verifyingKind, setVerifyingKind] = useState<VerificationKind | null>(null);

  // Documents
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadType, setUploadType] = useState<DocumentType>('GST_CERTIFICATE');
  const [uploadValidUntil, setUploadValidUntil] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [reviewDoc, setReviewDoc] = useState<{ id: number; status: DocumentReviewStatus } | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  // Reject dialog
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Approve dialog
  const [approveOpen, setApproveOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const full = await vendorOnboardingService.getFull(id);
      setApp(full);
    } catch (err: any) {
      setError(err.message || 'Failed to load application');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !app) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/procurement/vendor-onboarding')} sx={{ mb: 2 }}>
          Back to Vendor Onboarding
        </Button>
        <Alert severity="error">{error || 'Application not found'}</Alert>
      </Box>
    );
  }

  const flowIndex = FLOW_STEPS.indexOf(app.status);
  const isRejected = app.status === 'REJECTED';

  const canEdit = (['DRAFT', 'INVITED', 'SUBMITTED', 'UNDER_VERIFICATION'] as VendorOnboardingStatus[]).includes(
    app.status
  );
  const canInvite = (['DRAFT', 'INVITED'] as VendorOnboardingStatus[]).includes(app.status);
  const canSubmit = (['DRAFT', 'INVITED'] as VendorOnboardingStatus[]).includes(app.status);
  const canVerify = (['SUBMITTED', 'UNDER_VERIFICATION'] as VendorOnboardingStatus[]).includes(app.status);
  const canSubmitForApproval = app.status === 'UNDER_VERIFICATION';
  const canApprove = app.status === 'PENDING_APPROVAL';
  const canReject = (
    ['DRAFT', 'INVITED', 'SUBMITTED', 'UNDER_VERIFICATION', 'PENDING_APPROVAL'] as VendorOnboardingStatus[]
  ).includes(app.status);

  const runAction = async (name: string, fn: () => Promise<void>) => {
    setActionLoading(name);
    setActionError(null);
    try {
      await fn();
    } catch (err: any) {
      setActionError(err.message || `Failed to ${name}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleInvite = () =>
    runAction('invite', async () => {
      const result = await vendorOnboardingService.invite(app.id);
      setInviteUrl(result.inviteUrl);
      setNotice('Invite generated. Copy the link below — it will not be shown again.');
      await load();
    });

  const handleSubmit = () =>
    runAction('submit', async () => {
      await vendorOnboardingService.submit(app.id);
      await load();
    });

  const handleSubmitForApproval = () =>
    runAction('submit-for-approval', async () => {
      const outcome = await vendorOnboardingService.submitForApproval(app.id);
      setNotice(
        outcome.autoApproved
          ? `No approval workflow is configured for this tenant — application was auto-approved${
              outcome.vendorId ? ` and vendor #${outcome.vendorId} was created` : ''
            }.`
          : 'Submitted for approval.'
      );
      setNoticeVendorId(outcome.autoApproved ? outcome.vendorId ?? null : null);
      await load();
    });

  const handleApprove = () =>
    runAction('approve', async () => {
      const outcome = await vendorOnboardingService.approve(app.id, {});
      setNotice(`Application approved. Vendor #${outcome.vendorId} was created.`);
      setNoticeVendorId(outcome.vendorId ?? null);
      setApproveOpen(false);
      await load();
    });

  const handleReject = () =>
    runAction('reject', async () => {
      await vendorOnboardingService.reject(app.id, rejectReason.trim());
      setRejectOpen(false);
      setRejectReason('');
      await load();
    });

  const handleVerify = (kind: VerificationKind) => {
    setVerifyingKind(kind);
    setActionError(null);
    const body: { kind: VerificationKind; value?: string } = { kind };
    if (!AUTO_VALUE_KINDS.includes(kind)) {
      const value = (verifyValues[kind] || '').trim();
      if (!value) {
        setActionError(`Enter a value to verify ${VERIFICATION_LABEL[kind]}`);
        setVerifyingKind(null);
        return;
      }
      body.value = value;
    }
    vendorOnboardingService
      .verify(app.id, body)
      .then(async () => {
        await load();
      })
      .catch((err: any) => setActionError(err.message || `Failed to verify ${kind}`))
      .finally(() => setVerifyingKind(null));
  };

  const openEdit = () => {
    setEditForm({
      legalName: app.legal_name,
      tradeName: app.trade_name || '',
      contactPerson: app.contact_person || '',
      email: app.email,
      phone: app.phone || '',
      vendorType: app.vendor_type,
      gstin: app.gstin || '',
      pan: app.pan || '',
      udyamNumber: app.udyam_number || '',
      msmeCategory: app.msme_category || undefined,
    });
    setEditError(null);
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    setEditError(null);
    try {
      await vendorOnboardingService.update(app.id, editForm);
      setEditOpen(false);
      await load();
    } catch (err: any) {
      setEditError(err.message || 'Failed to update application');
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      setUploadError('Choose a file to upload');
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      await vendorOnboardingService.uploadDocument(app.id, uploadFile, uploadType, uploadValidUntil || null);
      setUploadOpen(false);
      setUploadFile(null);
      setUploadValidUntil('');
      await load();
    } catch (err: any) {
      setUploadError(err.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleReviewSubmit = async () => {
    if (!reviewDoc) return;
    setReviewSubmitting(true);
    try {
      await vendorOnboardingService.reviewDocument(reviewDoc.id, reviewDoc.status, reviewNotes.trim() || undefined);
      setReviewDoc(null);
      setReviewNotes('');
      await load();
    } catch (err: any) {
      setActionError(err.message || 'Failed to review document');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const latestVerificationByKind = new Map<VerificationKind, VendorOnboardingFull['verifications'][number]>();
  for (const v of app.verifications) {
    if (!latestVerificationByKind.has(v.kind)) latestVerificationByKind.set(v.kind, v);
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const in30Days = new Date(today);
  in30Days.setDate(in30Days.getDate() + 30);

  const documentExpiryFlag = (validUntil: string | null): { label: string; tone: StatusTone } | null => {
    if (!validUntil) return null;
    const d = new Date(validUntil);
    if (d < today) return { label: 'Expired', tone: 'error' };
    if (d <= in30Days) return { label: 'Expiring Soon', tone: 'warning' };
    return null;
  };

  const isMsmeStatutory = app.msme_category === 'MICRO' || app.msme_category === 'SMALL';

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/procurement/vendor-onboarding')} sx={{ mb: 1 }}>
        Back to Vendor Onboarding
      </Button>

      <PageHeader
        title={app.legal_name}
        qualifier={app.trade_name ? `(${app.trade_name})` : undefined}
        subtitle={`Reference ${app.reference_no} · ${app.email}`}
        actions={
          <>
            <StatusDot label={STATUS_LABEL[app.status]} tone={STATUS_TONE[app.status]} />
            <Button startIcon={<Edit />} size="small" onClick={openEdit} disabled={!canEdit}>
              Edit Details
            </Button>
          </>
        }
      />

      {notice && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => { setNotice(null); setNoticeVendorId(null); }}
          action={
            noticeVendorId ? (
              <Button color="inherit" size="small" onClick={() => navigate(`/procurement/vendors/${noticeVendorId}`)}>
                View Vendor
              </Button>
            ) : undefined
          }
        >
          {notice}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Stepper                                                          */}
      {/* ---------------------------------------------------------------- */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        {isRejected ? (
          <>
            <Stepper activeStep={-1} alternativeLabel sx={{ opacity: 0.45, mb: 2 }}>
              {FLOW_STEPS.map((s) => (
                <Step key={s}>
                  <StepLabel>{STATUS_LABEL[s]}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Alert severity="error" icon={<Cancel />}>
              <Typography sx={{ fontWeight: 700, mb: 0.25 }}>Application Rejected</Typography>
              <Typography variant="body2">{app.rejection_reason || 'No reason recorded.'}</Typography>
            </Alert>
          </>
        ) : (
          <Stepper activeStep={flowIndex} alternativeLabel>
            {FLOW_STEPS.map((s, i) => (
              <Step key={s} completed={i < flowIndex || (app.status === 'APPROVED' && i <= flowIndex)}>
                <StepLabel>{STATUS_LABEL[s]}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Paper>

      {/* ---------------------------------------------------------------- */}
      {/* Actions                                                          */}
      {/* ---------------------------------------------------------------- */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Workflow Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<MailOutline />}
            disabled={!canInvite || actionLoading !== null}
            onClick={handleInvite}
          >
            {actionLoading === 'invite' ? 'Sending...' : app.status === 'INVITED' ? 'Resend Invite' : 'Send Invite'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Send />}
            disabled={!canSubmit || actionLoading !== null}
            onClick={handleSubmit}
          >
            {actionLoading === 'submit' ? 'Submitting...' : 'Mark Submitted'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Task />}
            disabled={!canSubmitForApproval || actionLoading !== null}
            onClick={handleSubmitForApproval}
          >
            {actionLoading === 'submit-for-approval' ? 'Submitting...' : 'Submit for Approval'}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
            disabled={!canApprove || actionLoading !== null}
            onClick={() => setApproveOpen(true)}
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Cancel />}
            disabled={!canReject || actionLoading !== null}
            onClick={() => setRejectOpen(true)}
          >
            Reject
          </Button>
        </Box>

        {inviteUrl && (
          <Box
            sx={(theme) => ({
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'divider',
              backgroundColor: alpha(theme.palette.info.main, 0.06),
            })}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
              Invite link (copy now — shown only once; email delivery depends on SMTP being configured)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flexGrow: 1 }}
              >
                {inviteUrl}
              </Typography>
              <Tooltip title="Copy link">
                <IconButton size="small" onClick={() => navigator.clipboard?.writeText(inviteUrl)}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
      </Paper>

      <Grid container spacing={2.5}>
        {/* ---------------------------------------------------------------- */}
        {/* Applicant details                                                */}
        {/* ---------------------------------------------------------------- */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Applicant Details
            </Typography>
            <Grid container spacing={1.5}>
              {[
                ['Legal Name', app.legal_name],
                ['Trade Name', app.trade_name],
                ['Contact Person', app.contact_person],
                ['Email', app.email],
                ['Phone', app.phone],
                ['Vendor Type', app.vendor_type],
                ['GSTIN', app.gstin],
                ['PAN', app.pan],
                ['UDYAM Number', app.udyam_number],
                ['Created', fmtDateTime(app.created_at)],
                ['Submitted', fmtDateTime(app.submitted_at)],
                ['Approved', fmtDateTime(app.approved_at)],
              ].map(([label, value]) => (
                <Grid size={{ xs: 6 }} key={label}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {value || '—'}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* ---------------------------------------------------------------- */}
        {/* MSME panel                                                       */}
        {/* ---------------------------------------------------------------- */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              MSME Classification
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Category:
              </Typography>
              {app.msme_category ? (
                <Chip
                  size="small"
                  label={app.msme_category.replace('_', ' ')}
                  color={isMsmeStatutory ? 'warning' : 'default'}
                  variant={isMsmeStatutory ? 'filled' : 'outlined'}
                />
              ) : (
                <Typography variant="body2" color="text.disabled">
                  Not declared
                </Typography>
              )}
              {app.msme_verified_at && (
                <Typography variant="caption" color="text.secondary">
                  · UDYAM verified {fmtDateTime(app.msme_verified_at)}
                </Typography>
              )}
            </Box>

            {isMsmeStatutory ? (
              <Alert severity="warning" icon={<WarningAmber />}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                  Section 43B(h) applies — 45-day payment deadline
                </Typography>
                <Typography variant="body2">
                  This vendor is classified <strong>{app.msme_category}</strong>. Under Section 43B(h) of the
                  Income Tax Act, payment must be made within <strong>45 days</strong> of invoice/acceptance.
                  Paying late does not just delay the vendor — it <strong>disallows the expense as a tax
                  deduction</strong> for the payer in that financial year. Purchase invoices raised against this
                  vendor must be tracked against this deadline.
                </Typography>
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {app.msme_category === 'MEDIUM'
                  ? 'Classified MEDIUM — the 45-day Section 43B(h) deadline does not apply to MEDIUM enterprises.'
                  : app.msme_category === 'NOT_REGISTERED'
                  ? 'Vendor has declared they are not MSME-registered — Section 43B(h) does not apply.'
                  : 'MSME status has not been declared for this application yet.'}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* ---------------------------------------------------------------- */}
        {/* Verification panel                                               */}
        {/* ---------------------------------------------------------------- */}
        <Grid size={{ xs: 12 }}>
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Statutory Verification
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Offline format/checksum validation against a mock provider — not a live registry lookup. Bank
                account ownership cannot be confirmed without a real penny-drop, which is shown honestly as
                "Unavailable Offline" rather than a false pass.
              </Typography>
            </Box>
            <Table>
              <TableHead>
                <TableRow>
                  {['Kind', 'Value / Input', 'Result', 'Provider', 'Detail', ''].map((h) => (
                    <TableCell key={h} sx={headerCellSx}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(['GSTIN', 'PAN', 'UDYAM', 'BANK_ACCOUNT', 'IFSC'] as VerificationKind[]).map((kind) => {
                  const latest = latestVerificationByKind.get(kind);
                  const isAuto = AUTO_VALUE_KINDS.includes(kind);
                  const onFileValue =
                    kind === 'GSTIN' ? app.gstin : kind === 'PAN' ? app.pan : kind === 'UDYAM' ? app.udyam_number : null;
                  const disabled =
                    !canVerify || verifyingKind === kind || (isAuto && !onFileValue);

                  return (
                    <TableRow key={kind} sx={bodyRowSx}>
                      <TableCell sx={{ py: 1.5, fontWeight: 600 }}>{VERIFICATION_LABEL[kind]}</TableCell>
                      <TableCell sx={{ py: 1.5, minWidth: 200 }}>
                        {isAuto ? (
                          <Typography variant="body2" color={onFileValue ? 'text.primary' : 'text.disabled'}>
                            {onFileValue || 'Not on file'}
                          </Typography>
                        ) : (
                          <TextField
                            size="small"
                            placeholder={kind === 'IFSC' ? 'e.g. HDFC0001234' : 'Account number'}
                            value={verifyValues[kind] || ''}
                            onChange={(e) =>
                              setVerifyValues((prev) => ({ ...prev, [kind]: e.target.value.toUpperCase() }))
                            }
                            disabled={!canVerify}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {latest ? (
                          <StatusDot
                            label={VERIFICATION_STATUS_LABEL[latest.status]}
                            tone={VERIFICATION_TONE[latest.status]}
                          />
                        ) : (
                          <StatusDot label="Not Checked" tone="neutral" />
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {latest ? (
                          <Tooltip title="Which implementation produced this result — a mock/offline check must never be mistaken for a live government registry lookup">
                            <Chip size="small" variant="outlined" label={latest.provider} />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.5, maxWidth: 320 }}>
                        {latest ? (
                          <Typography variant="caption" color="text.secondary">
                            {latest.status === 'UNAVAILABLE' || latest.status === 'FAILED'
                              ? latest.failure_reason || '—'
                              : latest.legal_name_returned
                              ? `Matched: ${latest.legal_name_returned}`
                              : fmtDateTime(latest.verified_at)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }} align="right">
                        <Button
                          size="small"
                          startIcon={
                            verifyingKind === kind ? <CircularProgress size={14} /> : <VerifiedUser fontSize="small" />
                          }
                          disabled={disabled}
                          onClick={() => handleVerify(kind)}
                        >
                          Verify
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {!canVerify && (
              <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">
                  Verification is only available while the application is Submitted or Under Verification.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* ---------------------------------------------------------------- */}
        {/* Documents panel                                                  */}
        {/* ---------------------------------------------------------------- */}
        <Grid size={{ xs: 12 }}>
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box
              sx={{
                p: 2.5,
                pb: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                KYC Documents
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadFile />}
                onClick={() => {
                  setUploadFile(null);
                  setUploadValidUntil('');
                  setUploadError(null);
                  setUploadOpen(true);
                }}
              >
                Upload Document
              </Button>
            </Box>
            <Table>
              <TableHead>
                <TableRow>
                  {['Type', 'File', 'Valid Until', 'Status', 'Reviewed', ''].map((h) => (
                    <TableCell key={h} sx={headerCellSx}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {app.documents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ border: 0 }}>
                      <TableEmptyState title="No documents uploaded yet" hint="Upload PAN, GST, UDYAM or bank proof." />
                    </TableCell>
                  </TableRow>
                ) : (
                  app.documents.map((doc) => {
                    const expiry = documentExpiryFlag(doc.valid_until);
                    return (
                      <TableRow key={doc.id} sx={bodyRowSx}>
                        <TableCell sx={{ py: 1.5 }}>{DOCUMENT_TYPE_LABEL[doc.document_type]}</TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2">{doc.file_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(doc.size_bytes / 1024).toFixed(0)} KB
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2">{fmtDate(doc.valid_until)}</Typography>
                          {expiry && (
                            <Chip
                              size="small"
                              sx={{ mt: 0.5 }}
                              color={expiry.tone === 'error' ? 'error' : 'warning'}
                              label={expiry.label}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <StatusDot label={doc.status} tone={DOCUMENT_STATUS_TONE[doc.status] || 'neutral'} />
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          {doc.reviewed_at ? (
                            <Tooltip title={doc.review_notes || 'No notes'}>
                              <Typography variant="caption" color="text.secondary">
                                {fmtDateTime(doc.reviewed_at)}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          {doc.status === 'PENDING' && (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <Tooltip title="Mark verified">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => {
                                    setReviewDoc({ id: doc.id, status: 'VERIFIED' });
                                    setReviewNotes('');
                                  }}
                                >
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject document">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setReviewDoc({ id: doc.id, status: 'REJECTED' });
                                    setReviewNotes('');
                                  }}
                                >
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>

      {/* ================================================================ */}
      {/* Dialogs                                                          */}
      {/* ================================================================ */}

      {/* Edit applicant */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Applicant Details</DialogTitle>
        <DialogContent dividers>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Legal Name"
                fullWidth
                value={editForm.legalName || ''}
                onChange={(e) => setEditForm({ ...editForm, legalName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Trade Name"
                fullWidth
                value={editForm.tradeName || ''}
                onChange={(e) => setEditForm({ ...editForm, tradeName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Email"
                fullWidth
                value={editForm.email || ''}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Phone"
                fullWidth
                value={editForm.phone || ''}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Contact Person"
                fullWidth
                value={editForm.contactPerson || ''}
                onChange={(e) => setEditForm({ ...editForm, contactPerson: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Vendor Type"
                fullWidth
                value={editForm.vendorType || 'COMPANY'}
                onChange={(e) => setEditForm({ ...editForm, vendorType: e.target.value as VendorType })}
              >
                {VENDOR_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="GSTIN"
                fullWidth
                value={editForm.gstin || ''}
                onChange={(e) => setEditForm({ ...editForm, gstin: e.target.value.toUpperCase() })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="PAN"
                fullWidth
                value={editForm.pan || ''}
                onChange={(e) => setEditForm({ ...editForm, pan: e.target.value.toUpperCase() })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="UDYAM Number"
                fullWidth
                value={editForm.udyamNumber || ''}
                onChange={(e) => setEditForm({ ...editForm, udyamNumber: e.target.value.toUpperCase() })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="MSME Category"
                fullWidth
                value={editForm.msmeCategory || ''}
                onChange={(e) =>
                  setEditForm({ ...editForm, msmeCategory: (e.target.value || undefined) as ApplicantFields['msmeCategory'] })
                }
              >
                <MenuItem value="">Not specified</MenuItem>
                {MSME_CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c.replace('_', ' ')}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload document */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Upload KYC Document</DialogTitle>
        <DialogContent dividers>
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}
          <TextField
            select
            label="Document Type"
            fullWidth
            sx={{ mb: 2 }}
            value={uploadType}
            onChange={(e) => setUploadType(e.target.value as DocumentType)}
          >
            {DOCUMENT_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {DOCUMENT_TYPE_LABEL[t]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Valid Until (optional)"
            type="date"
            fullWidth
            sx={{ mb: 2 }}
            value={uploadValidUntil}
            onChange={(e) => setUploadValidUntil(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button component="label" variant="outlined" startIcon={<UploadFile />} fullWidth>
            {uploadFile ? uploadFile.name : 'Choose File (PDF, JPG, PNG)'}
            <input
              type="file"
              hidden
              accept="application/pdf,image/jpeg,image/png"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={uploading}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review document */}
      <Dialog open={reviewDoc !== null} onClose={() => setReviewDoc(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{reviewDoc?.status === 'VERIFIED' ? 'Verify Document' : 'Reject Document'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Notes (optional)"
            fullWidth
            multiline
            rows={3}
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDoc(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={reviewDoc?.status === 'VERIFIED' ? 'success' : 'error'}
            onClick={handleReviewSubmit}
            disabled={reviewSubmitting}
          >
            {reviewSubmitting ? 'Saving...' : reviewDoc?.status === 'VERIFIED' ? 'Mark Verified' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject application */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Application</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            A rejection reason is required and will be shown to whoever created this application.
          </DialogContentText>
          <TextField
            label="Rejection Reason"
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={rejectReason.trim().length < 3 || actionLoading === 'reject'}
          >
            {actionLoading === 'reject' ? 'Rejecting...' : 'Reject Application'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve application */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Approve Application</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Approving will create a new <strong>Vendor Master</strong> record from this application's legal
            name, GSTIN/PAN and contact details, generate a vendor code, and mark this onboarding{' '}
            <strong>APPROVED</strong>. The applicant's contact person, email and phone will be used as the
            primary vendor contact. This cannot be undone from this screen.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApprove} disabled={actionLoading === 'approve'}>
            {actionLoading === 'approve' ? 'Approving...' : 'Approve & Create Vendor'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorOnboardingDetailPage;
